import React, { useEffect, useRef, useState } from "react";
import {
  FaQrcode, FaMobileAlt, FaWhatsapp,
  FaUserPlus, FaUtensils, FaPrint, FaRocket
} from "react-icons/fa";

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

    .hiw-root *, .hiw-root *::before, .hiw-root *::after { box-sizing:border-box; }
    .hiw-root { font-family:'Plus Jakarta Sans',sans-serif; }
    .hiw-zh   { font-family:'Fraunces',Georgia,serif; }

    /* ── Scroll reveal ── */
    .reveal {
      opacity:0;
      transform:translateY(28px);
      transition:opacity .6s cubic-bezier(.22,.68,0,1.2), transform .6s cubic-bezier(.22,.68,0,1.2);
    }
    .reveal.visible { opacity:1; transform:translateY(0); }
    .reveal-l {
      opacity:0; transform:translateX(-28px);
      transition:opacity .55s cubic-bezier(.22,.68,0,1.2), transform .55s cubic-bezier(.22,.68,0,1.2);
    }
    .reveal-l.visible { opacity:1; transform:translateX(0); }
    .reveal-r {
      opacity:0; transform:translateX(28px);
      transition:opacity .55s cubic-bezier(.22,.68,0,1.2), transform .55s cubic-bezier(.22,.68,0,1.2);
    }
    .reveal-r.visible { opacity:1; transform:translateX(0); }

    /* Stagger delays */
    .d1{ transition-delay:.05s } .d2{ transition-delay:.15s }
    .d3{ transition-delay:.25s } .d4{ transition-delay:.35s }

    /* ── Customer step card ── */
    .cust-card {
      position:relative; overflow:hidden;
      background:#fff; border:1.5px solid #efefea;
      border-radius:20px; padding:32px 28px 28px;
      transition:border-color .22s, box-shadow .22s, transform .22s;
      flex:1; min-width:0;
    }
    .cust-card:hover {
      border-color:#6cbb23;
      box-shadow:0 16px 48px rgba(108,187,35,.1);
      transform:translateY(-3px);
    }
    .cust-card:hover .cust-ghost { opacity:.07; }
    .cust-ghost {
      position:absolute; bottom:-20px; right:-8px;
      font-size:120px; font-weight:900; line-height:1;
      color:#6cbb23; opacity:.04;
      font-family:'Fraunces',serif; font-style:italic;
      user-select:none; pointer-events:none;
      transition:opacity .22s;
    }
    .cust-icon-wrap {
      width:48px; height:48px; border-radius:14px;
      background:#f0f9e6; border:1.5px solid #d4efb5;
      display:flex; align-items:center; justify-content:center;
      margin-bottom:20px;
      transition:background .22s, border-color .22s;
    }
    .cust-card:hover .cust-icon-wrap {
      background:#6cbb23; border-color:#6cbb23;
    }
    .cust-card:hover .cust-icon { color:#fff !important; }

    /* ── Connector arrow ── */
    .step-connector {
      display:flex; align-items:center; justify-content:center;
      flex-shrink:0; width:40px; padding-top:28px;
    }
    .connector-line {
      width:100%; height:1.5px;
      background:repeating-linear-gradient(90deg, #d4efb5 0, #d4efb5 6px, transparent 6px, transparent 12px);
      position:relative;
    }
    .connector-line::after {
      content:'';
      position:absolute; right:-1px; top:50%;
      transform:translateY(-50%);
      border-left:7px solid #6cbb23;
      border-top:5px solid transparent;
      border-bottom:5px solid transparent;
    }

    /* ── Business zigzag ── */
    .biz-row {
      display:grid;
      grid-template-columns:1fr 56px 1fr;
      align-items:center;
      gap:0;
      position:relative;
      padding:20px 0;
    }
    .biz-node {
      width:44px; height:44px; border-radius:50%;
      background:#6cbb23; border:4px solid #fafaf8;
      box-shadow:0 0 0 2px #6cbb23;
      display:flex; align-items:center; justify-content:center;
      margin:0 auto; position:relative; z-index:2;
      flex-shrink:0;
    }
    .biz-content {
      background:#fff; border:1.5px solid #efefea;
      border-radius:16px; padding:24px 26px;
      transition:border-color .22s, box-shadow .22s;
    }
    .biz-content:hover {
      border-color:#6cbb23;
      box-shadow:0 10px 32px rgba(108,187,35,.09);
    }
    .biz-empty { /* placeholder for the empty side */ }
    .biz-time-badge {
      display:inline-flex; align-items:center; gap:5px;
      background:#f0f9e6; border:1px solid #c8e89c;
      border-radius:999px; padding:3px 10px;
      font-size:11px; font-weight:600; color:#3b6d11;
      font-family:'Plus Jakarta Sans',sans-serif;
      margin-top:10px;
    }

    /* ── Section label (rotated) ── */
    .section-label {
      display:inline-flex; align-items:center; gap:8px;
      background:#f0f9e6; border:1.5px solid #c8e89c;
      border-radius:999px; padding:5px 16px;
      font-size:11px; font-weight:600; color:#3b6d11;
      letter-spacing:.08em; text-transform:uppercase;
      font-family:'Plus Jakarta Sans',sans-serif;
    }

    /* ── Divider ── */
    .section-divider {
      display:flex; align-items:center; gap:16px;
      margin:56px 0 48px;
    }
    .section-divider::before, .section-divider::after {
      content:''; flex:1; height:1.5px; background:#efefea;
    }

    /* ── Footer pill ── */
    .footer-pill {
      display:inline-flex; align-items:center; gap:8px;
      background:#fff; border:1.5px solid #efefea;
      border-radius:999px; padding:10px 24px;
      font-size:13px; font-weight:500; color:#555;
      font-family:'Plus Jakarta Sans',sans-serif;
      transition:border-color .18s;
    }
    .footer-pill:hover { border-color:#6cbb23; }

    /* ── Mobile overrides ── */
    @media(max-width:768px) {
      .cust-flow-row { flex-direction:column !important; gap:0 !important; }
      .step-connector { display:none !important; }
      .cust-card { margin-bottom:12px; }
      .cust-card:last-child { margin-bottom:0; }

      .biz-row { grid-template-columns:36px 1fr; grid-template-rows:auto; gap:0 14px; padding:0; }
      .biz-left-content  { grid-column:2; grid-row:1; }
      .biz-right-content { grid-column:2; grid-row:1; }
      .biz-node { grid-column:1; grid-row:1; width:36px; height:36px; }
      .biz-empty { display:none !important; }
      .biz-content { padding:18px 20px; }
      .biz-timeline-line { left:18px !important; }
    }

    @media(max-width:480px) {
      .hiw-heading { font-size:clamp(32px,8vw,44px) !important; }
      .cust-ghost { font-size:80px !important; }
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   SCROLL REVEAL HOOK
───────────────────────────────────────────── */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ─────────────────────────────────────────────
   CUSTOMER STEP CARD
───────────────────────────────────────────── */
const CustomerCard = ({ icon: Icon, num, title, desc, delay }: {
  icon: React.ElementType; num: string; title: string; desc: string; delay: string;
}) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal cust-card ${delay}`}>
      <div className="cust-icon-wrap">
        <Icon className="cust-icon" style={{ color:"#6cbb23", fontSize:18 }} />
      </div>
      <div style={{ marginBottom:6, display:"flex", alignItems:"baseline", gap:8 }}>
        <span className="hiw-zh" style={{ fontSize:11, fontWeight:800, color:"#6cbb23", letterSpacing:".1em" }}>
          {num}
        </span>
        <h4 className="hiw-zh" style={{ fontSize:19, fontWeight:800, color:"#111", fontStyle:"italic", lineHeight:1.1 }}>
          {title}
        </h4>
      </div>
      <p style={{ fontSize:13, color:"#888", lineHeight:1.7, fontWeight:300 }}>{desc}</p>
      <div className="cust-ghost">{num}</div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   BUSINESS STEP ROW (zigzag)
───────────────────────────────────────────── */
const BizRow = ({ icon: Icon, num, title, desc, time, flip, delay }: {
  icon: React.ElementType; num: string; title: string;
  desc: string; time: string; flip: boolean; delay: string;
}) => {
  const refL = useReveal();
  const refR = useReveal();

  const content = (
    <div className="biz-content">
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
        <div style={{ width:36, height:36, borderRadius:10, background:"#f0f9e6", border:"1.5px solid #d4efb5", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Icon style={{ color:"#6cbb23", fontSize:15 }} />
        </div>
        <div>
          <span style={{ fontSize:10, fontWeight:700, color:"#aaa", letterSpacing:".1em", textTransform:"uppercase", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Step {num}</span>
          <h4 className="hiw-zh" style={{ fontSize:18, fontWeight:800, color:"#111", fontStyle:"italic", lineHeight:1.1, marginTop:2 }}>{title}</h4>
        </div>
      </div>
      <p style={{ fontSize:13, color:"#888", lineHeight:1.7, fontWeight:300 }}>{desc}</p>
      <div className="biz-time-badge">
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><circle cx="4.5" cy="4.5" r="4" stroke="#3b6d11" strokeWidth="1"/><path d="M4.5 2.5v2.2l1.2 1" stroke="#3b6d11" strokeWidth="1" strokeLinecap="round"/></svg>
        {time}
      </div>
    </div>
  );

  const node = (
    <div className="biz-node">
      <span className="hiw-zh" style={{ fontSize:14, fontWeight:900, color:"#fff" }}>{num}</span>
    </div>
  );

  /* Desktop: content left or right depending on flip */
  return (
    <div className="biz-row">
      {/* Left cell */}
      <div ref={refL} className={`reveal-l ${delay} ${flip ? "biz-left-content" : "biz-empty"}`}>
        {flip ? content : null}
      </div>

      {/* Centre node */}
      <div style={{ display:"flex", justifyContent:"center" }}>
        {node}
      </div>

      {/* Right cell */}
      <div ref={refR} className={`reveal-r ${delay} ${flip ? "biz-empty" : "biz-right-content"}`}>
        {flip ? null : content}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const customerSteps = [
  { icon:FaQrcode,    num:"01", title:"Scan the QR",   desc:"Customers scan the QR placed on your table, counter, or room. No app download, no friction." },
  { icon:FaMobileAlt, num:"02", title:"Browse & Pick",  desc:"They explore your digital menu with images, descriptions, prices, and customisation options." },
  { icon:FaWhatsapp,  num:"03", title:"Order Arrives",  desc:"Orders land instantly on your WhatsApp and dashboard — ready to action in seconds." },
];

const businessSteps = [
  { icon:FaUserPlus,  num:"01", title:"Sign Up Free",      desc:"Create your Zafflo account in under 60 seconds. No credit card, no commitment.",     time:"~60 sec"  },
  { icon:FaUtensils,  num:"02", title:"Build Your Menu",   desc:"Add items, upload photos, set prices, and categorise in any Indian language.",         time:"~5 min"   },
  { icon:FaPrint,     num:"03", title:"Get Your QR Code",  desc:"Download your personalised QR menu. Print it, frame it, or display it digitally.",      time:"~30 sec"  },
  { icon:FaRocket,    num:"04", title:"Go Live",           desc:"Place the QR and watch orders, bookings, and customer insights start flowing in.",       time:"Instant"  },
];

const HowItWorks = () => {
  const headerRef  = useReveal();
  const divRef     = useReveal();
  const bizHeadRef = useReveal();
  const footerRef  = useReveal();

  return (
    <section className="hiw-root" style={{ background:"#fafaf8", position:"relative", overflow:"hidden", padding:"50px 0 42px" }}>
      <Styles />

      {/* Subtle background grid */}
      <div style={{
        position:"absolute", inset:0, pointerEvents:"none",
        backgroundImage:"linear-gradient(#efefea 1px, transparent 1px), linear-gradient(90deg, #efefea 1px, transparent 1px)",
        backgroundSize:"48px 48px", opacity:.5
      }} />

      {/* Top green glow */}
      <div style={{ position:"absolute", top:-120, left:"50%", transform:"translateX(-50%)", width:600, height:400, background:"radial-gradient(ellipse, rgba(108,187,35,.07) 0%, transparent 70%)", pointerEvents:"none" }} />

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"0 24px", position:"relative", zIndex:1 }}>

        {/* ── SECTION HEADER ── */}
        <div ref={headerRef} className="reveal" style={{ textAlign:"center", marginBottom:56 }}>
         
          <h2 className="hiw-zh hiw-heading" style={{ fontSize:"clamp(34px,4vw,52px)", fontWeight:900, fontStyle:"italic", color:"#111", letterSpacing:"-0.03em", lineHeight:1.26, marginBottom:16 }}>
            Simple to start.{" "}
            <mark style={{ background:"#6cbb23", color:"#fff", padding:"1px 12px 5px", borderRadius:6, fontStyle:"italic", display:"inline-block" }}>
              Powerful to grow.
            </mark>
          </h2>
          <p style={{ fontSize:15, color:"#888", fontWeight:300, maxWidth:420, margin:"0 auto", lineHeight:1.7 }}>
            Zero training for your customers. A 5-minute setup for your business.
          </p>
        </div>

        {/* ─────────────────────────────────
            CUSTOMER JOURNEY
        ───────────────────────────────── */}
        <div style={{ marginBottom:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:32 }}>
            <span style={{ fontSize:12, fontWeight:600, color:"#bbb", letterSpacing:".1em", textTransform:"uppercase" }}>For Your Customers</span>
            <div style={{ flex:1, height:1, background:"#efefea" }} />
            <span style={{ fontSize:12, color:"#ccc" }}>3 steps</span>
          </div>

          {/* Step row + connectors */}
          <div className="cust-flow-row" style={{ display:"flex", alignItems:"stretch", gap:0 }}>
            {customerSteps.map((step, i) => (
              <React.Fragment key={step.num}>
                <CustomerCard {...step} delay={["d1","d2","d3"][i]} />
                {i < customerSteps.length - 1 && (
                  <div className="step-connector" style={{ width:48, flexShrink:0, display:"flex", alignItems:"center", paddingBottom:8 }}>
                    <div className="connector-line" style={{ width:"100%" }} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ─────────────────────────────────
            DIVIDER
        ───────────────────────────────── */}
        <div ref={divRef} className="reveal section-divider">
          <span style={{ fontSize:12, color:"#ccc", fontWeight:500, letterSpacing:".05em", whiteSpace:"nowrap" }}>· · ·</span>
        </div>

        {/* ─────────────────────────────────
            BUSINESS JOURNEY
        ───────────────────────────────── */}
        <div>
          <div ref={bizHeadRef} className="reveal" style={{ display:"flex", alignItems:"center", gap:12, marginBottom:8 }}>
            <span style={{ fontSize:12, fontWeight:600, color:"#bbb", letterSpacing:".1em", textTransform:"uppercase" }}>For Your Business</span>
            <div style={{ flex:1, height:1, background:"#efefea" }} />
            <span style={{ fontSize:12, color:"#ccc" }}>4 steps</span>
          </div>

          {/* Timeline spine wrapper */}
          <div style={{ position:"relative" }}>
            {/* Vertical line */}
            <div className="biz-timeline-line" style={{
              position:"absolute", left:"50%", top:22, bottom:22,
              width:2,
              background:"repeating-linear-gradient(180deg, #c8e89c 0, #c8e89c 8px, transparent 8px, transparent 16px)",
              transform:"translateX(-50%)", zIndex:1
            }} />

            {businessSteps.map((step, i) => (
              <BizRow key={step.num} {...step} flip={i % 2 === 0} delay={["d1","d2","d3","d4"][i]} />
            ))}
          </div>
        </div>

        {/* ─────────────────────────────────
            FOOTER PILL
        ───────────────────────────────── */}
        <div ref={footerRef} className="reveal" style={{ marginTop:52, display:"flex", justifyContent:"center", gap:12, flexWrap:"wrap" }}>
          <span className="footer-pill">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6.25" stroke="#6cbb23" strokeWidth="1.5"/><path d="M7 4v3.5l2 1.5" stroke="#6cbb23" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Setup takes less than 5 minutes
          </span>
          <span className="footer-pill">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l3.5 3.5L12 3.5" stroke="#6cbb23" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            No hardware required
          </span>
          <span className="footer-pill">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5C4 1.5 1.5 4 1.5 7S4 12.5 7 12.5 12.5 10 12.5 7 10 1.5 7 1.5z" stroke="#6cbb23" strokeWidth="1.5"/><path d="M5 7l1.5 1.5L9.5 5" stroke="#6cbb23" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Works on any smartphone
          </span>
        </div>

      </div>
    </section>
  );
};

export default HowItWorks;